import type {
    ClientPositionView,
    ClientProgramSurfaceOption,
    CreatePositionInput,
    MultisigPreflightResponse
} from "@solomon-apps/shared";

import {
    clientProgramSurfaceToOption,
    createClientProgramPosition,
    fallbackPositionViewFromProgramPosition,
    fetchClientPrograms
} from "./clientPrograms";
import { orgApiFetch, type PaginatedResponse } from "./orgApiFetch";

type ClientPositionWireView = Omit<ClientPositionView, "programSurface"> & {
    programSurface?: ClientPositionView["programSurface"];
    pool?: ClientPositionView["programSurface"];
};

// Response contract lives in @solomon-apps/shared (squadsMultisig.ts),
// shared with the issuer console. Re-exported so existing imports keep working.
export type { MultisigPreflightResponse };

/* ── Earnings types ──────────────────────────────────────── */

export interface DailyEarning {
    timestampMs: number;
    cumulativeEarnedUsdv: number;
    apyPct: number;
    dailyBalanceUsdv: number;
}

export interface HeadlineFigures {
    trailingApyPct: number;
    apyDeltaPct: number;
    earnedWindowUsdv: number;
    earnedTotalUsdv: number;
    earnedDeltaUsdv: number;
}

export interface ClientEarningsResponse {
    daily: DailyEarning[];
    headline: HeadlineFigures;
    snapshotAt: string;
}

export function fetchClientEarnings(): Promise<ClientEarningsResponse> {
    return orgApiFetch<ClientEarningsResponse>("/api/org/client/earnings");
}

/* ── Distribution types ─────────────────────────────────── */

export interface DistributionItem {
    uuid: string;
    distributionDate: string;
    amountUsdv: number;
    positionUuid?: string;
    positionName?: string;
    pool: string;
    sourceWallet: string;
    destinationWallet: string;
    txSignature: string;
}

export function fetchClientDistributions(params?: {
    limit?: number;
    page?: number;
    sortOrder?: "ASC" | "DESC";
}): Promise<PaginatedResponse<DistributionItem>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.page) query.set("page", String(params.page));
    if (params?.sortOrder) query.set("sortOrder", params.sortOrder);
    const qs = query.toString();
    return orgApiFetch<PaginatedResponse<DistributionItem>>(`/api/org/client/distributions${qs ? `?${qs}` : ""}`);
}

/** Page size for `fetchAllClientDistributions`. */
const DISTRIBUTION_PAGE_SIZE = 200;
/** Ceiling on pages walked, so a misreported `total` can't loop forever. */
const DISTRIBUTION_PAGE_CAP = 50;

/**
 * Every finalized payout, oldest first, walking the paginated endpoint.
 * Reporting sums payouts into running totals, so a truncated first page
 * would silently understate every cumulative figure after it.
 */
export async function fetchAllClientDistributions(): Promise<DistributionItem[]> {
    const items: DistributionItem[] = [];
    for (let page = 1; page <= DISTRIBUTION_PAGE_CAP; page++) {
        const response = await fetchClientDistributions({
            limit: DISTRIBUTION_PAGE_SIZE,
            page,
            sortOrder: "ASC"
        });
        items.push(...response.data);
        if (items.length >= response.total) return items;
        if (response.data.length === 0) break;
    }
    throw new Error("Could not load complete payment history.");
}

/* ── Program Surfaces ───────────────────────────────────── */

/**
 * The Program Surfaces this client can hold a position on.
 *
 * This also required `surfaceCategory.participationMode ===
 * "ClientControlledPositions"` until yaas-api retired the participation
 * classification and dropped its columns. The field stopped being serialized,
 * so the comparison matched nothing and this returned an empty list for every
 * client — emptying the Add Position picker and defeating the live-balance
 * lookup that falls back to this catalog.
 *
 * Manual creation and automatic wallet enrollment are separate server
 * capabilities. A program's surfaces are offered when the server grants either.
 */
export async function fetchClientProgramSurfaces(): Promise<ClientProgramSurfaceOption[]> {
    const result = await fetchClientPrograms();
    return result.data.flatMap((programView) =>
        programView.canCreateProgramPosition || programView.canEnrollWallet
            ? programView.surfaces.map(clientProgramSurfaceToOption)
            : []
    );
}

function normalizeClientPositionView(raw: ClientPositionWireView): ClientPositionView {
    const { pool, programSurface, ...position } = raw;
    return {
        ...position,
        programSurface: programSurface ??
            pool ?? {
                uuid: raw.uuid,
                name: "Program Surface",
                type: "PROGRAM",
                address: raw.account.address,
                trailingApyPct: null,
                product: "Program Surface",
                surface: null
            }
    };
}

/** Page size for `fetchClientPositions`; one request covers almost every client. */
const POSITION_PAGE_SIZE = 100;
/** Ceiling on pages walked, so a misreported `total` can't loop forever. */
const POSITION_PAGE_CAP = 20;

/**
 * Every position for the client, walking the paginated endpoint. yaas-api
 * materializes one row per observed LP position per Program, so a client with
 * several pools passes the default page of 50 and would silently lose rows.
 */
export async function fetchClientPositions(status?: string, rewardHistory?: "all"): Promise<ClientPositionView[]> {
    if (rewardHistory === "all") {
        const positions: ClientPositionView[] = [];
        for (let page = 1; page <= 50; page++) {
            const query = new URLSearchParams({ rewardHistory, limit: "200", page: String(page) });
            if (status) query.set("status", status);
            const result = await orgApiFetch<PaginatedResponse<ClientPositionWireView>>(
                `/api/org/client/positions?${query}`
            );
            positions.push(...result.data.map(normalizeClientPositionView));
            if (positions.length >= result.total) return positions;
            if (!result.data.length) break;
        }
        throw new Error("The complete position history could not be loaded.");
    }
    const items: ClientPositionWireView[] = [];
    for (let page = 1; page <= POSITION_PAGE_CAP; page++) {
        const query = new URLSearchParams({ limit: String(POSITION_PAGE_SIZE), page: String(page) });
        if (status) query.set("status", status);
        const response = await orgApiFetch<PaginatedResponse<ClientPositionWireView>>(
            `/api/org/client/positions?${query.toString()}`
        );
        items.push(...response.data);
        if (response.data.length === 0 || items.length >= response.total) break;
    }
    return items.map(normalizeClientPositionView);
}

export async function createClientPosition(input: CreatePositionInput): Promise<ClientPositionView> {
    const position = await createClientProgramPosition({
        programSurfaceUuid: input.programSurfaceUuid,
        walletAddress: input.walletAddress,
        rewardsDestinationAddress: input.rewardsDestinationAddress,
        ...(input.claimRetailAccount ? { claimRetailAccount: true } : {}),
        ...(input.label !== undefined ? { label: input.label } : {}),
        ...(input.verificationMethod ? { verificationMethod: input.verificationMethod } : {}),
        ...(input.verificationMode ? { verificationMode: input.verificationMode } : {}),
        ...(input.manualReviewDescription ? { manualReviewDescription: input.manualReviewDescription } : {}),
        ...(input.verificationMessage ? { verificationMessage: input.verificationMessage } : {}),
        ...(input.verificationSignature ? { verificationSignature: input.verificationSignature } : {}),
        ...(input.verificationSignedMessage ? { verificationSignedMessage: input.verificationSignedMessage } : {}),
        ...(input.verificationNonce ? { verificationNonce: input.verificationNonce } : {}),
        ...(input.sourceKind ? { sourceKind: input.sourceKind } : {}),
        ...(input.signerAddress ? { signerAddress: input.signerAddress } : {}),
        ...(input.multisigProgramId ? { multisigProgramId: input.multisigProgramId } : {})
    });

    const [positions, programSurfaces] = await Promise.all([
        fetchClientPositions().catch(() => []),
        fetchClientProgramSurfaces().catch(() => [])
    ]);
    return (
        positions.find((candidate) => candidate.uuid === position.uuid) ??
        fallbackPositionViewFromProgramPosition({
            position,
            input: {
                programSurfaceUuid: input.programSurfaceUuid,
                walletAddress: input.walletAddress,
                rewardsDestinationAddress: input.rewardsDestinationAddress,
                label: input.label,
                verificationMethod: input.verificationMethod,
                verificationMode: input.verificationMode,
                manualReviewDescription: input.manualReviewDescription,
                verificationMessage: input.verificationMessage,
                verificationSignature: input.verificationSignature,
                sourceKind: input.sourceKind,
                signerAddress: input.signerAddress,
                multisigProgramId: input.multisigProgramId
            },
            programSurface: programSurfaces.find((surface) => surface.uuid === input.programSurfaceUuid)
        })
    );
}

export function preflightMultisig(args: {
    address: string;
    signerAddress: string | null;
    signal?: AbortSignal;
}): Promise<MultisigPreflightResponse> {
    return orgApiFetch<MultisigPreflightResponse>("/api/org/client/positions/multisig-preflight", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            address: args.address,
            ...(args.signerAddress ? { signerAddress: args.signerAddress } : {})
        }),
        signal: args.signal
    });
}

export interface KnownMultisig {
    multisigAddress: string;
    vaultAddress: string | null;
    programId: string;
    memberCount: number;
    threshold: number;
    signerStatus: "can-verify" | "cannot-verify" | "not-member" | "no-signer";
    usdvBalance: string | null;
    lastUsedAt: string | null;
}

export interface MultisigsHistoryResponse {
    multisigs: KnownMultisig[];
}

export function fetchMultisigsHistory(args: { signerAddress: string | null }): Promise<MultisigsHistoryResponse> {
    return orgApiFetch<MultisigsHistoryResponse>("/api/org/client/positions/multisigs-history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            signerAddress: args.signerAddress
        })
    });
}

export function resubmitPosition(uuid: string): Promise<ClientPositionView> {
    return orgApiFetch<ClientPositionWireView>(`/api/org/client/positions/${uuid}/resubmit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
    }).then(normalizeClientPositionView);
}
